import React, { useEffect, useState } from 'react';

import { AnimatePresence, useMotionValue, useSpring } from 'framer-motion';

import { Thumbnail } from '@pkg/components/Thumbnail';

import LogoSvg from '../../Navigation/assets/icons/logo.svg?react';

import {
    BackgroundPink,
    BackgroundPurple,
    CubeInside,
    CubeWrapper,
    Face,
    FaceInside,
    FaceOutside,
    //Image,
    MediaHolder,
    Perspective,
    Wrapper,
    YatHand,
} from './styles';

type Face = 'front' | 'left' | 'back' | 'right' | 'top' | 'bottom';
const faces: Face[] = ['front', 'left', 'back', 'right', 'top', 'bottom'];

interface Props {
    width: number;
    data?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    disableHover?: boolean;
    isLoading?: boolean;
    isDragging: boolean;
    setIsDragging: (isDragging: boolean) => void;
    isHovering: boolean;
    setIsHovering: (isHovering: boolean) => void;
    showDelay?: number;
    skipIntro?: boolean;
    currentFaceIndex?: number;
    setCurrentFaceIndex?: (index: number) => void;
    freezeCube?: boolean;
    setFreezeCube?: (freezeCube: boolean) => void;
    isPageOwner?: boolean;
    hideBackground?: boolean;
}

export const Cube = ({
    width,
    data,
    disableHover,
    isDragging,
    setIsDragging,
    isHovering,
    setIsHovering,
    showDelay,
    skipIntro,
    currentFaceIndex,
    // setCurrentFaceIndex,
    freezeCube,
    setFreezeCube,
    hideBackground,
}: Props) => {
    const [show, setShow] = useState(false);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setShow(true);
        }, showDelay);

        return () => clearTimeout(timeoutId);
    }, [showDelay]);

    let springSettings = {
        damping: 600,
        stiffness: 600,
        mass: 100,
        restDelta: 0.1,
        restSpeed: 1,
    };

    if (width <= 160) {
        springSettings = {
            damping: 300,
            stiffness: 300,
            mass: 50,
            restDelta: 0.1,
            restSpeed: 1,
        };
    }

    const rotationX = useSpring(useMotionValue(-12), springSettings);
    const rotationY = useSpring(useMotionValue(-50), springSettings);

    useEffect(() => {
        if (currentFaceIndex !== undefined && !isDragging) {
            let targetRotationX: number;
            let targetRotationY: number;

            if (currentFaceIndex === 5) {
                targetRotationX = 90;
                targetRotationY = 0;
            } else if (currentFaceIndex === 4) {
                targetRotationX = -90;
                targetRotationY = 0;
            } else {
                targetRotationX = -12;
                targetRotationY = currentFaceIndex * 90;
            }

            const currentRotationY = rotationY.get();
            const difference = targetRotationY - currentRotationY;

            if (difference > 180) {
                targetRotationY -= 360;
            } else if (difference < -180) {
                targetRotationY += 360;
            }

            rotationY.set(targetRotationY);
            rotationX.set(targetRotationX);
        }
    }, [currentFaceIndex, rotationY, rotationX, isDragging]);

    return (
        <Wrapper
            $width={width}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            $isHovering={(isHovering || isDragging) && !disableHover}
        >
            <AnimatePresence>
                {show && (
                    <Perspective
                        initial={{ opacity: 0, scale: 0.3 }}
                        animate={{ opacity: 1, scale: 0.7 }}
                        exit={{ opacity: 0, scale: 0.3 }}
                        transition={{ duration: skipIntro ? 0 : 1.5, ease: 'anticipate' }}
                    >
                        <CubeWrapper
                            drag
                            dragConstraints={{ top: 0, right: 0, bottom: 0, left: 0 }}
                            style={{ rotateX: rotationX, rotateY: rotationY }}
                            onDrag={(_, info) => {
                                rotationX.set(-info.offset.y);
                                rotationY.set(info.offset.x);

                                /*
                                // Disabling this for now, its not perfect 

                                let sideIndex = 0;
                                const xRotation = Math.round(info.offset.y / 90);
                                const yRotation = Math.round(info.offset.x / 90);

                                if (xRotation % 4 === 0) {
                                    sideIndex = ((yRotation % 4) + 4) % 4;
                                } else if (xRotation % 4 === 2 || xRotation % 4 === -2) {
                                    sideIndex = ((-yRotation % 4) + 4) % 4;
                                } else if (xRotation % 4 === 1 || xRotation % 4 === -3) {
                                    sideIndex = 4; // Top face
                                } else {
                                    sideIndex = 5; // Bottom face
                                }

                                if (setCurrentFaceIndex) {
                                    if (data[sideIndex]) setCurrentFaceIndex(sideIndex);
                                }
                                */
                            }}
                            onDragStart={() => {
                                setIsDragging(true);
                                setFreezeCube && setFreezeCube(false);
                            }}
                            onDragEnd={() => {
                                setIsDragging(false);
                                rotationX.set(-12);
                                setFreezeCube && setFreezeCube(false);
                            }}
                            whileTap={{ cursor: 'grabbing' }}
                        >
                            <CubeInside $pause={isDragging} $freeze={freezeCube}>
                                {faces.map((face, index) => (
                                    <Face $face={face} $width={width} key={`face-${face}`} data-face={face}>
                                        <FaceOutside $width={width}>
                                            {data && data[index] && (
                                                //<Image $url={data[index].image_url || ''} $width={width} />
                                                <MediaHolder $width={width}>
                                                    <Thumbnail
                                                        autoPlay
                                                        videoUrl={data[index].animation_url || ''}
                                                        imageUrl={data[index].image_url || ''}
                                                        mashWidth={width}
                                                        objectFitContain
                                                    />
                                                </MediaHolder>
                                            )}
                                        </FaceOutside>
                                        <FaceInside $width={width} />
                                    </Face>
                                ))}
                                <YatHand>
                                    <LogoSvg />
                                </YatHand>
                            </CubeInside>
                        </CubeWrapper>
                    </Perspective>
                )}
            </AnimatePresence>
            {!hideBackground && <BackgroundPink $width={width} />}
            {!hideBackground && <BackgroundPurple $width={width} />}
        </Wrapper>
    );
};
